// 장비 연출(c017, 장비 시스템 c009가 남긴 VFX 훅): 바닥 드롭 줍기 순간과 전설 고유 효과 발동을 VFX 모듈 라이브러리로 그린다.
// 새 드로우콜은 3차 모듈 '유도 줄기'(streak) 하나뿐이고, 나머지는 기둥·고리·오라·화면 모듈과 불똥 입자를 같이 쓴다(모듈당 1회).
// - Ev.LootPickup: 등급 색 빛기둥이 아래로 접히며(0.16초) 밝은 매듭이 되고, 그 자리에서 나선 줄기가 영웅 가슴으로 빨려 든다.
//   머리가 닿는 순간 영웅 발밑에 작은 고리 + 가슴 높이 작은 불똥(영웅 위를 덮는 큰 것은 없다). 세트(녹)·전설(금)은 두 가닥 나선(이중 나선),
//   드롭 자리에서 굵은 기둥이 접히며 바닥 고리가 퍼지고 불똥이 솟으며, 도착 고리·불똥이 더 크다(다른 등급과 다른 박자). 강화석은 낮고 작은 청회색.
// - Ev.GearUnique: 회피의 방패 = 영웅을 감싸는 짧은 강철 거품(오라) + 발밑 고리 / 처치 회복·MP·각성 = 처치 자리에서 영웅으로 오르는 작은 알갱이
//   (녹·청·잿불) / 벼랑 끝 칼날 = 화면 가장자리 옅은 붉은 맥동(화면 모듈 → 렌더가 꽂은 공용 FlashLimiter.admitSoft를 반드시 거친다, 1.5초에 한 번).
//   칼날의 박자(치명타 재사용 감소)는 스킬 바 칸의 시계 바늘 반짝임이라 UI(ui/skillbar.ts)가 그린다.
// - 이름 있는 유니크(BALANCE-1028 3절, l1029): 표의 칸 5(주황 #ff8c2e). Ev.LootDrop flags & 1 = 떨어지는 순간 '하늘에서 내리꽂는' 연출
//   (아래 namedDrop 박자표: 0초 바닥 균열 룬·솟는 기둥·충격파 → 0.12초 두 번째 고리 → 0.3초 높은 기둥이 다시 솟고 불똥 분수),
//   Ev.LootPickup flags & 2 = 전설과 같은 두 가닥 줍기를 주황으로. 화면 효과는 가장자리 옅은 주황 맥동 한 번(공용 섬광 예산 통과, 섬광 약화면 생략).
// 혼전(방 정리 일괄 수거·대량 처치)에서는 시간 창 예산으로 줄기·알갱이 수를 막는다(전설·세트 줍기는 예산과 관계없이 그린다).
// 피는 쓰지 않는다(12+). 프레임 루프·이벤트 처리 안에서 새 객체를 만들지 않는다(프리셋·대상 핸들·도착 대기열은 생성 때 고정).
import { GRADE_BEAM, NAMED_BEAM, NAMED_FX_INDEX } from '../shared/content/loot';
import { GearCue, uniqueCueOf } from '../shared/gearCues';
import { ConsumeKind, Ev, type GameEvent } from '../shared/events';
import type { ParticleFx } from './particles';
import { makeTarget, StreakModule, type FxPreset, type FxTarget, type VfxModules } from './modules';

/** 바닥 드롭 빛기둥 높이(m)·폭 배율(등급 번호 순, 칸 5 = 이름 있는 유니크 — 전설보다 높고 굵다). render/loot.ts 기둥과 접히는 기둥이 같은 값을 쓴다 */
export const LOOT_BEAM_H = [1.2, 1.8, 2.4, 3.2, 4.2, 5.4] as const;
export const LOOT_BEAM_W = [0.7, 0.85, 1, 1.15, 1.35, 1.55] as const;
/** 강화석 결정 색 */
export const STONE_COLOR = 0x9fc3e6;
const GRADE_COLOR = [GRADE_BEAM.common, GRADE_BEAM.rare, GRADE_BEAM.epic, GRADE_BEAM.set, GRADE_BEAM.legend, NAMED_BEAM] as const;
/** 머리·심 색(등급 색을 흰빛 쪽으로) */
const GRADE_HOT = [0xffffff, 0x9cc8ff, 0xd4a8ff, 0x8cffb4, 0xffe08a, 0xffd2a0] as const;
/** 도착·솟구침 불똥 HDR 색 */
const GRADE_SPARK: readonly (readonly [number, number, number])[] = [
  [2.2, 2.2, 2.2], [0.8, 1.6, 3.2], [2.0, 1.0, 3.2], [0.8, 3.0, 1.4], [3.4, 2.3, 0.6], [3.6, 1.5, 0.35],
];
/** 처치 효과 알갱이 출발 불똥 HDR 색 */
const MOTE_SPARK = { heal: [0.5, 3.0, 1.0], mp: [0.5, 1.4, 3.2], awaken: [3.2, 1.2, 0.4] } as const;
const STEEL = 0x6f92c8;
const STEEL_WHITE = 0xd8e6ff;

/** 줄기 이동 시간(초): 일반·전설/세트·강화석·알갱이 */
export const PICKUP_TRAVEL = { normal: 0.36, big: 0.42, stone: 0.3, mote: 0.34 } as const;
const TAIL = { pickup: 0.5, mote: 0.6 } as const;
/** 줄기 머리가 닿는 높이(대상 기준, 가슴) */
const CHEST = 1.05;

/** 시간 창 예산: 창(초) 안에 띄우는 수 상한 */
export const GEAR_FX_BUDGET = {
  /** 일반~영웅 등급·강화석 줍기 줄기(방 정리 일괄 수거). 넘으면 접히는 기둥만 */
  pickup: { window: 0.25, max: 4 },
  /** 처치 효과 알갱이(대량 처치 한 순간에 수십 개가 나지 않게) */
  mote: { window: 0.15, max: 3 },
} as const;
/** 같은 효과 최소 간격(초) */
/** 방패는 0.8초에 한 번(PR #161 리뷰 P2: 연속 회피 때 거품이 영웅을 거의 계속 덮었다) */
export const GEAR_FX_GAP = { shield: 0.8, lowHp: 1.5, namedFlash: 1.2 } as const;
/**
 * 이름 있는 유니크 떨어짐 박자표(초, LootDrop 시각 기준). 보스 처치 컷 뒤 바닥에 꽂히는 '와!' 순간 — 높은 기둥 두 번(낮고 굵게 → 높고 가늘게),
 * 충격파 고리 두 겹, 바닥 균열 룬, 불똥 분수. 섬광은 가장자리 맥동 한 번뿐(1초 3회 한도는 공용 FlashLimiter가 지킨다).
 */
export const NAMED_DROP_BEATS = { ring2: 0.12, pillar2: 0.3 } as const;
const NAMED = NAMED_FX_INDEX;
/**
 * 줄기 모듈 칸 지키기(PR #161 리뷰 P2): 장비 연출은 가득 찬 줄기 모듈에 띄우지 않는다(살아 있는 줄기를 밀어내지 않는다 — 전설 줄기 보호).
 * 일반 줍기·강화석·알갱이는 빈 칸이 reserve보다 많을 때만 쓴다(세트·전설 몫을 남긴다). 세트·전설은 동시 2건까지 두 가닥, 그 뒤로는 한 가닥
 */
export const STREAK_GUARD = { reserve: 3, bigPairs: 2 } as const;
/** 벼랑 끝 칼날 가장자리 맥동 세기: 옅은 비네트 문턱(SOFT_PULSE 0.12) 위라 공용 섬광 예산을 거친다 */
export const LOW_HP_PULSE = 0.22;
/** 도착 대기열 칸 수 */
const ARRIVE_CAP = 10;

function pickupPresets(): { column: FxPreset[]; strand: FxPreset[]; strand2: FxPreset[]; dropRing: FxPreset[]; arrive: FxPreset[] } {
  const column: FxPreset[] = [], strand: FxPreset[] = [], strand2: FxPreset[] = [], dropRing: FxPreset[] = [], arrive: FxPreset[] = [];
  for (let g = 0; g <= NAMED_FX_INDEX; g++) {
    const big = g >= 3;
    const c = GRADE_COLOR[g], h = GRADE_HOT[g];
    const T = big ? PICKUP_TRAVEL.big : PICKUP_TRAVEL.normal;
    column.push({ variant: 1, y: 0.05, length: LOOT_BEAM_H[g], width: (big ? 0.8 : 0.55) * LOOT_BEAM_W[g], life: big ? 0.22 : 0.16, color: c, color2: h, intensity: big ? 2.4 : 1.7 + 0.15 * g, seed: 0.2 * g });
    strand.push({ y: 0.38, length: CHEST, width: big ? 0.3 : 0.18 + 0.03 * g, from: 0.5 + 0.06 * g, sweep: Math.PI * 2.5, start: 0, roll: 0.55, tail: TAIL.pickup,
      life: T * (1 + TAIL.pickup), color: c, color2: h, intensity: big ? 2.6 : 2.0 + 0.2 * g, seed: 0.13 + 0.1 * g, age0: 0 });
    strand2.push({ ...strand[g], start: Math.PI, seed: 0.61 + 0.1 * g });
    dropRing.push({ y: 0, length: 1.7, from: 0.12, width: 0.34, life: 0.38, color: c, color2: h, intensity: 1.8, seed: 0.5 });
    arrive.push({ y: 0, length: big ? 1.35 : 0.85, from: 0.25, width: big ? 0.28 : 0.18, life: big ? 0.45 : 0.32, color: c, color2: h, intensity: big ? 2.0 : 1.6, seed: 0.7 });
  }
  return { column, strand, strand2, dropRing, arrive };
}

export const GEAR_FX_PRESETS = {
  ...pickupPresets(),
  stoneColumn: { variant: 1, y: 0.05, length: 0.7, width: 0.22, life: 0.12, color: STONE_COLOR, color2: 0xeaf4ff, intensity: 1.3, seed: 0.9 },
  stoneStrand: { y: 0.25, length: CHEST, width: 0.08, from: 0.3, sweep: Math.PI * 1.5, start: 0, roll: 0.4, tail: TAIL.pickup,
    life: PICKUP_TRAVEL.stone * (1 + TAIL.pickup), color: STONE_COLOR, color2: 0xeaf4ff, intensity: 1.3, seed: 0.33 },
  /** 회피의 방패: 짧은 강철 거품(오라 0.5초)과 발밑 고리 */
  shieldAura: { y: 0.05, length: 2.0, width: 0.72, life: 0.45, color: STEEL, color2: STEEL_WHITE, intensity: 0.85, seed: 0.4 },
  shieldRing: { y: 0, length: 1.25, from: 0.55, width: 0.16, life: 0.3, color: STEEL, color2: STEEL_WHITE, intensity: 1.3, seed: 0.25 },
  /** 처치 효과 알갱이(색만 다르다) */
  moteHeal: { y: 0.9, length: CHEST, width: 0.13, from: 0.22, sweep: 3.2, start: 0, roll: 0.8, tail: TAIL.mote, life: PICKUP_TRAVEL.mote * (1 + TAIL.mote), color: 0x22e060, color2: 0x8cffb0, intensity: 2.6, seed: 0.15 },
  moteMp: { y: 0.9, length: CHEST, width: 0.13, from: 0.22, sweep: -3.2, start: 0, roll: 0.8, tail: TAIL.mote, life: PICKUP_TRAVEL.mote * (1 + TAIL.mote), color: 0x2a7cff, color2: 0x9ad0ff, intensity: 2.6, seed: 0.45 },
  moteAwaken: { y: 0.9, length: CHEST, width: 0.13, from: 0.22, sweep: 3.2, start: 1.6, roll: 0.8, tail: TAIL.mote, life: PICKUP_TRAVEL.mote * (1 + TAIL.mote), color: 0xff4a1a, color2: 0xffb060, intensity: 2.6, seed: 0.75 },
  /**
   * 소비 아이템(D2c, Ev.Consume): 영웅 발밑 작은 고리(물약 HP 녹·MP 청, 비약 계열 색, 부활 깃털 금빛 큰 고리 + 오라).
   * 화면 모듈·섬광은 쓰지 않는다(밝기 급변 없음 — 섬광 약화 설정과 무관하게 안전). 섬광 약화면 불똥 수만 줄인다
   */
  potionRing: [
    { y: 0, length: 1.15, from: 0.3, width: 0.17, life: 0.42, color: 0x22d860, color2: 0x9cffc0, intensity: 1.5, seed: 0.31 },
    { y: 0, length: 1.15, from: 0.3, width: 0.17, life: 0.42, color: 0x2a7cff, color2: 0xa8d4ff, intensity: 1.5, seed: 0.37 },
  ],
  elixirRing: [
    { y: 0, length: 1.4, from: 0.4, width: 0.2, life: 0.5, color: 0xff5a3a, color2: 0xffc08a, intensity: 1.6, seed: 0.51 },
    { y: 0, length: 1.4, from: 0.4, width: 0.2, life: 0.5, color: 0x6f92c8, color2: 0xd8e6ff, intensity: 1.6, seed: 0.53 },
    { y: 0, length: 1.4, from: 0.4, width: 0.2, life: 0.5, color: 0x3fd07a, color2: 0xb4ffd0, intensity: 1.6, seed: 0.57 },
    { y: 0, length: 1.4, from: 0.4, width: 0.2, life: 0.5, color: 0xa45cff, color2: 0xe0c4ff, intensity: 1.6, seed: 0.59 },
  ],
  reviveRing: { y: 0, length: 2.2, from: 0.2, width: 0.3, life: 0.7, color: 0xf2b233, color2: 0xfff1b8, intensity: 2.0, seed: 0.66 },
  reviveAura: { y: 0.05, length: 2.2, width: 0.8, life: 0.8, color: 0xf2b233, color2: 0xfff1b8, intensity: 0.9, seed: 0.62 },
  /** 이름 있는 유니크 떨어짐(namedDrop): 굵게 솟는 첫 기둥 · 높고 가는 두 번째 기둥 · 충격파 두 겹 · 바닥 방사 균열 룬 · 가장자리 주황 맥동 */
  namedPillar: { y: 0, length: 3.6, width: 0.7, life: 0.75, color: NAMED_BEAM, color2: 0xffc890, intensity: 1.8, seed: 0.37 },
  // 두 번째 기둥은 경계색을 주황 쪽에 두고 밝기를 낮춘다(실제 장면 캡처에서 3.0·흰빛 경계가 블룸으로 하얗게 타서 전설 금색과 구분이 흐렸다)
  namedPillar2: { y: 0, length: 7.5, width: 0.32, life: 1.1, color: NAMED_BEAM, color2: 0xffb070, intensity: 1.6, seed: 0.81 },
  namedShock: { y: 0.02, length: 3.4, from: 0.1, width: 0.42, life: 0.55, color: NAMED_BEAM, color2: 0xffd2a0, intensity: 2.2, seed: 0.21 },
  namedShock2: { y: 0.02, length: 2.2, from: 0.2, width: 0.26, life: 0.45, color: 0xffb060, color2: 0xfff1d8, intensity: 1.9, seed: 0.64 },
  namedRune: { variant: 1, y: 0, length: 1.9, life: 1.4, color: NAMED_BEAM, color2: 0xffe2b8, intensity: 1.8, seed: 0.47 },
  namedEdge: { variant: 0, color: NAMED_BEAM, intensity: 0.2, life: 0.7 },
  /** 벼랑 끝 칼날: 가장자리만(variant 0) 옅은 붉은 맥동 */
  lowHpPulse: { variant: 0, color: 0xd0101e, intensity: LOW_HP_PULSE, life: 0.9 },
} as const satisfies Record<string, FxPreset | FxPreset[]>;

export class GearFx {
  /** QA 집계 */
  readonly stats = {
    pickups: 0, pickupStrands: 0, pickupBig: 0, pickupThrottled: 0, arrivals: 0,
    uniques: 0, shields: 0, motes: 0, moteThrottled: 0, lowHp: 0, lowHpBlocked: 0, refunds: 0, streakFull: 0, bigSingle: 0, consumes: 0,
    namedDrops: 0, namedPickups: 0, namedEdge: 0, namedEdgeBlocked: 0,
  };
  /** 영웅 핸들(렌더가 매 프레임 setHero로 옮긴다): 줄기·거품이 쫓는다 */
  readonly hero: FxTarget = makeTarget();
  private t = 0;
  private scale = 1;
  private reduceFlash = false;
  private pickWin = -1e9; private pickN = 0;
  /** 진행 중인 세트·전설 줍기의 끝 시각(두 가닥 동시 상한 판정) */
  private readonly bigEnd = new Float64Array(8);
  private moteWin = -1e9; private moteN = 0;
  private lastShield = -1e9;
  private lastLowHp = -1e9;
  private lastNamedEdge = -1e9;
  /** 이름 있는 유니크 박자 대기(두 번째 고리·기둥·분수): 드롭 자리와 시각. 동시에 4건까지(보스 한 마리에서 많아야 2개 — 넘치면 그 드롭은 0초 박자만 나고 게임은 그대로) */
  private readonly namedAt = new Float64Array(4);
  private readonly namedX = new Float32Array(4);
  private readonly namedZ = new Float32Array(4);
  private readonly namedStage = new Uint8Array(4);
  private namedN = 0;
  private readonly arriveAt = new Float64Array(ARRIVE_CAP);
  private readonly arriveGrade = new Int8Array(ARRIVE_CAP);
  private arriveN = 0;

  constructor(private readonly mods: VfxModules, private readonly particles: ParticleFx | null) {}

  /** 품질 배율(0..1): 불똥 수에 곱한다 */
  setScale(s: number): void { this.scale = Math.max(0, Math.min(1, Number.isFinite(s) ? s : 1)); }
  /** 섬광 약화(게임 설정): 가장자리 맥동을 끈다(화면 모듈 세기 감쇠와 별개로 아예 요청하지 않는다) */
  setReduceFlash(on: boolean): void { this.reduceFlash = on; }

  /** 영웅 위치(바닥 기준 y). 렌더가 영웅 갱신 뒤 매 프레임 부른다 */
  setHero(x: number, y: number, z: number): void { this.hero.x = x; this.hero.y = y; this.hero.z = z; }

  /** 렌더가 Ev.LootPickup·Ev.GearUnique를 넘긴다. 반환: 무엇이든 띄웠으면 true */
  onEvent(e: GameEvent): boolean {
    if (e.type === Ev.LootDrop) return e.a === 0 && (e.flags & 1) !== 0 ? this.namedDrop(e.x, e.z) : false;
    if (e.type === Ev.LootPickup) return this.pickup(e.x, e.z, e.a, e.a === 0 && (e.flags & 2) !== 0 ? NAMED : e.b);
    if (e.type === Ev.GearUnique) return this.unique(e.a, e.x, e.z);
    if (e.type === Ev.Consume) return this.consume(e.a, e.b);
    return false;
  }

  /** 소비 아이템 연출(D2c). kind = ConsumeKind, b = 비약 번호(비약일 때). 영웅을 따라 발밑 고리 + 작은 불똥(섬광 없음) */
  consume(kind: number, b: number): boolean {
    const P = GEAR_FX_PRESETS, h = this.hero, ps = this.particles;
    const sparks = ps && this.scale > 0 ? Math.max(1, Math.round((this.reduceFlash ? 2 : 5) * this.scale)) : 0;
    if (kind === ConsumeKind.HpPotion || kind === ConsumeKind.MpPotion) {
      const mp = kind === ConsumeKind.MpPotion;
      this.mods.play('ring', P.potionRing[mp ? 1 : 0], h.x, h.y, h.z, 0);
      if (sparks) { const c = mp ? MOTE_SPARK.mp : MOTE_SPARK.heal; ps!.streaks(h.x, h.y + 0.3, h.z, 0, Math.PI * 2, sparks, 1.0, c[0], c[1], c[2], 0.3, 0.45, 2.2); }
      this.stats.consumes++;
      return true;
    }
    if (kind === ConsumeKind.Elixir) {
      this.mods.play('ring', P.elixirRing[Math.max(0, Math.min(3, b | 0))], h.x, h.y, h.z, 0);
      this.stats.consumes++;
      return true;
    }
    if (kind === ConsumeKind.Feather) {
      this.mods.play('ring', P.reviveRing, h.x, h.y, h.z, 0);
      this.mods.playAura(P.reviveAura, this.hero);
      if (sparks) ps!.streaks(h.x, h.y + 0.4, h.z, 0, Math.PI * 2, sparks * 2, 1.8, 3.4, 2.3, 0.6, 0.4, 0.6, 4.0);
      this.stats.consumes++;
      return true;
    }
    return false;
  }

  /**
   * 이름 있는 유니크가 바닥에 떨어진 순간(0초 박자): 굵은 기둥이 솟고 충격파·바닥 룬이 퍼진다. 0.12초·0.3초 박자는 update가 잇는다.
   * 줄기 모듈은 쓰지 않는다(줍기 줄기 칸을 빼앗지 않는다). 프레임 루프 밖(이벤트)이지만 새 객체는 만들지 않는다
   */
  namedDrop(x: number, z: number): boolean {
    const P = GEAR_FX_PRESETS;
    this.stats.namedDrops++;
    this.mods.play('pillar', P.namedPillar, x, 0, z, 0);
    this.mods.play('ring', P.namedShock, x, 0, z, 0);
    this.mods.play('crack', P.namedRune, x, 0, z, 0);
    const ps = this.particles;
    if (ps && this.scale > 0) { const c = GRADE_SPARK[NAMED]; ps.streaks(x, 0.3, z, 0, Math.PI * 2, Math.max(1, Math.round(14 * this.scale)), 2.2, c[0], c[1], c[2], 0.25, 0.5, 5.0); }
    // 가장자리 주황 맥동: 섬광 약화면 요청하지 않고, 켜져 있어도 공용 섬광 예산(FlashLimiter)이 거절하면 그대로 둔다
    if (!this.reduceFlash && this.t - this.lastNamedEdge >= GEAR_FX_GAP.namedFlash) {
      if (this.mods.play('screen', P.namedEdge, 0, 0, 0, 0)) { this.lastNamedEdge = this.t; this.stats.namedEdge++; } else this.stats.namedEdgeBlocked++;
    }
    if (this.namedN < this.namedAt.length) {
      const k = this.namedN++;
      this.namedAt[k] = this.t; this.namedX[k] = x; this.namedZ[k] = z; this.namedStage[k] = 0;
    }
    return true;
  }

  /** 박자 대기 처리: 0 → 두 번째 고리(0.12초) → 1 → 높은 기둥·불똥 분수(0.3초) → 끝 */
  private namedBeats(): void {
    const P = GEAR_FX_PRESETS, B = NAMED_DROP_BEATS;
    let i = 0;
    while (i < this.namedN) {
      const dt = this.t - this.namedAt[i], x = this.namedX[i], z = this.namedZ[i];
      if (this.namedStage[i] === 0 && dt >= B.ring2) { this.mods.play('ring', P.namedShock2, x, 0, z, 0); this.namedStage[i] = 1; }
      if (this.namedStage[i] === 1 && dt >= B.pillar2) {
        this.mods.play('pillar', P.namedPillar2, x, 0, z, 0);
        const ps = this.particles;
        // 기둥을 따라 솟는 불똥 분수(위로 좁게 — 바깥으로 퍼지지 않아 플레이 영역을 덮지 않는다)
        if (ps && this.scale > 0) { const c = GRADE_SPARK[NAMED]; ps.streaks(x, 0.5, z, 0, Math.PI * 2, Math.max(1, Math.round(22 * this.scale)), 1.0, c[0], c[1], c[2], 0.5, 0.7, 9.0); }
        const j = --this.namedN;
        this.namedAt[i] = this.namedAt[j]; this.namedX[i] = this.namedX[j]; this.namedZ[i] = this.namedZ[j]; this.namedStage[i] = this.namedStage[j];
        continue;
      }
      i++;
    }
  }

  /** 줍기: kind 0 장비 / 1 강화석, grade 0..4(5 = 이름 있는 유니크) */
  pickup(x: number, z: number, kind: number, grade: number): boolean {
    this.stats.pickups++;
    const P = GEAR_FX_PRESETS;
    // 강화석·보석(D2c): 작은 결정이 빨려 드는 같은 연출
    if (kind === 1 || kind === 2) {
      this.streak(P.stoneColumn, x, z, null, STREAK_GUARD.reserve);
      if (!this.takePick()) { this.stats.pickupThrottled++; return true; }
      if (!this.streak(P.stoneStrand, x, z, this.hero, STREAK_GUARD.reserve)) return true;
      this.stats.pickupStrands++;
      this.queueArrive(PICKUP_TRAVEL.stone, -1);
      return true;
    }
    const g = grade < 0 ? 0 : grade > NAMED ? NAMED : grade | 0;
    const big = g >= 3;
    if (g === NAMED) this.stats.namedPickups++;
    this.streak(P.column[g], x, z, null, big ? 1 : STREAK_GUARD.reserve);
    if (big) {
      // 세트·전설: 예산과 관계없이 두 가닥 + 기둥 솟구침 + 바닥 고리
      this.stats.pickupBig++;
      this.mods.play('ring', P.dropRing[g], x, 0, z, 0);
      // 드롭 자리에서 등급 색 불똥이 솟는다(기둥이 터지며 접히는 느낌)
      const ps = this.particles;
      if (ps && this.scale > 0) { const sc = GRADE_SPARK[g]; ps.streaks(x, 0.4, z, 0, Math.PI * 2, Math.max(1, Math.round(16 * this.scale)), 1.6, sc[0], sc[1], sc[2], 0.4, 0.55, 6.5); }
      // 동시에 진행 중인 세트·전설 줍기가 bigPairs건 이상이면 한 가닥만(줄기 모듈 상한을 넘겨 다른 줄기를 밀어내지 않게)
      let live = 0, slot = 0;
      for (let i = 0; i < this.bigEnd.length; i++) { if (this.bigEnd[i] > this.t) live++; else slot = i; }
      this.bigEnd[slot] = this.t + (P.strand[g].life ?? 0);
      if (this.streak(P.strand[g], x, z, this.hero, 0)) this.stats.pickupStrands++;
      if (live < STREAK_GUARD.bigPairs) { if (this.streak(P.strand2[g], x, z, this.hero, 0)) this.stats.pickupStrands++; }
      else this.stats.bigSingle++;
      this.queueArrive(PICKUP_TRAVEL.big, g);
      return true;
    }
    if (!this.takePick()) { this.stats.pickupThrottled++; return true; }
    if (!this.streak(P.strand[g], x, z, this.hero, STREAK_GUARD.reserve)) return true;
    this.stats.pickupStrands++;
    this.queueArrive(PICKUP_TRAVEL.normal, g);
    return true;
  }

  /** 전설 고유 효과(a = LEGEND_UNIQUES 번호). x,z = 영웅 위치(처치 효과는 처치 자리) */
  unique(a: number, x: number, z: number): boolean {
    this.stats.uniques++;
    const P = GEAR_FX_PRESETS;
    switch (uniqueCueOf(a)) {
      case GearCue.Shield: {
        if (this.t - this.lastShield < GEAR_FX_GAP.shield) return false;
        this.lastShield = this.t;
        this.mods.playAura(P.shieldAura, this.hero);
        this.mods.play('ring', P.shieldRing, this.hero.x, this.hero.y, this.hero.z, 0);
        this.stats.shields++;
        return true;
      }
      case GearCue.Heal: return this.mote(P.moteHeal, x, z, MOTE_SPARK.heal);
      case GearCue.Mp: return this.mote(P.moteMp, x, z, MOTE_SPARK.mp);
      case GearCue.Awaken: return this.mote(P.moteAwaken, x, z, MOTE_SPARK.awaken);
      case GearCue.LowHp: {
        if (this.reduceFlash || this.t - this.lastLowHp < GEAR_FX_GAP.lowHp) return false;
        // 세기가 SOFT_PULSE 위라 화면 모듈이 렌더가 꽂은 공용 섬광 예산(FlashLimiter.admitSoft)에 묻는다 — 우회하지 않는다
        if (!this.mods.play('screen', P.lowHpPulse, 0, 0, 0, 0)) { this.stats.lowHpBlocked++; return false; }
        this.lastLowHp = this.t;
        this.stats.lowHp++;
        return true;
      }
      case GearCue.Refund: this.stats.refunds++; return false;
      default: return false;
    }
  }

  private mote(preset: FxPreset, x: number, z: number, spark: readonly [number, number, number]): boolean {
    if (this.t - this.moteWin >= GEAR_FX_BUDGET.mote.window) { this.moteWin = this.t; this.moteN = 0; }
    if (this.moteN >= GEAR_FX_BUDGET.mote.max) { this.stats.moteThrottled++; return false; }
    this.moteN++;
    if (!this.streak(preset, x, z, this.hero, STREAK_GUARD.reserve)) return false;
    // 처치 자리에서 같은 색 알갱이 몇 개가 솟는다(떠오르는 출발 표시, 작다)
    const ps = this.particles;
    if (ps && this.scale > 0) ps.streaks(x, 0.9, z, 0, Math.PI * 2, Math.max(1, Math.round(3 * this.scale)), 0.8, spark[0], spark[1], spark[2], 0.2, 0.35, 3.0);
    this.stats.motes++;
    return true;
  }

  /** 줄기 하나(빈 칸이 reserve보다 많을 때만 — 가득 찬 모듈에서 살아 있는 줄기를 밀어내지 않는다). 반환: 띄웠으면 true */
  private streak(preset: FxPreset, x: number, z: number, target: FxTarget | null, reserve: number): boolean {
    const m = this.mods.streak;
    if (m.capacity - m.activeCount <= reserve) { this.stats.streakFull++; return false; }
    return this.mods.playStreak(preset, x, 0, z, target);
  }

  private takePick(): boolean {
    if (this.t - this.pickWin >= GEAR_FX_BUDGET.pickup.window) { this.pickWin = this.t; this.pickN = 0; }
    if (this.pickN >= GEAR_FX_BUDGET.pickup.max) return false;
    this.pickN++;
    return true;
  }

  /** 줄기 머리가 닿을 시각에 도착 연출을 건다. grade −1 = 강화석 */
  private queueArrive(travel: number, grade: number): void {
    if (this.arriveN >= ARRIVE_CAP) return;
    this.arriveAt[this.arriveN] = this.t + travel;
    this.arriveGrade[this.arriveN] = grade;
    this.arriveN++;
  }

  private arrive(grade: number): void {
    this.stats.arrivals++;
    const h = this.hero, ps = this.particles;
    if (grade < 0) {
      if (ps && this.scale > 0) ps.streaks(h.x, h.y + CHEST, h.z, 0, Math.PI * 2, Math.max(1, Math.round(3 * this.scale)), 1.2, 0.9, 1.4, 2.0, 0.5, 0.25, 1.8);
      return;
    }
    const big = grade >= 3;
    this.mods.play('ring', GEAR_FX_PRESETS.arrive[grade], h.x, h.y, h.z, 0);
    if (ps && this.scale > 0) {
      const c = GRADE_SPARK[grade];
      ps.streaks(h.x, h.y + CHEST, h.z, 0, Math.PI * 2, Math.max(1, Math.round((big ? 20 : 9) * this.scale)), big ? 2.6 : 1.8, c[0], c[1], c[2], big ? 0.35 : 0.5, big ? 0.45 : 0.32, big ? 3.6 : 2.6);
    }
  }

  /** dt초(영웅 시계) 진행: 도착 대기열을 처리한다(도착 자리는 그 순간의 영웅 위치) */
  update(dt: number): void {
    this.t += dt;
    if (this.namedN) this.namedBeats();
    let i = 0;
    while (i < this.arriveN) {
      if (this.t >= this.arriveAt[i]) {
        const g = this.arriveGrade[i];
        const j = --this.arriveN;
        this.arriveAt[i] = this.arriveAt[j]; this.arriveGrade[i] = this.arriveGrade[j];
        this.arrive(g);
        continue;
      }
      i++;
    }
  }

  /** 장면 전환: 대기 중인 도착 연출을 버린다(모듈 자체는 렌더가 비운다) */
  clear(): void {
    this.arriveN = 0;
    this.namedN = 0;
    this.pickN = 0; this.moteN = 0;
    this.bigEnd.fill(0);
  }

  /** 지금 살아 있는 줄기 수(QA) */
  get strands(): number { return this.mods.streak.activeCount; }
}

/** 줄기 이동 시간 = 수명 / (1 + 꼬리)(시험·문서용 재수출) */
export const streakTravel = StreakModule.travel;
